import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../app/store';
import { promotionApi, type Placement } from '../billing/promotionApi';
import { OrganizationCardView, ProductCardView } from './components';

function SponsoredCard({ placement }: { placement: Placement }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node || !('IntersectionObserver' in window)) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let sent = false;
    const clear = () => { clearTimeout(timer); timer = undefined; };
    const observer = new IntersectionObserver(entries => {
      clear();
      if (entries.some(e => e.isIntersecting && e.intersectionRatio >= 0.5) && !document.hidden && !sent)
        timer = setTimeout(() => {
          if (document.hidden) return;
          sent = true;
          void promotionApi.view(placement.id).catch(() => { sent = false; });
        }, 1000);
    }, { threshold: 0.5 });
    observer.observe(node);
    const visibility = () => {
      clear();
      if (!document.hidden && !sent) { observer.unobserve(node); observer.observe(node); }
    };
    document.addEventListener('visibilitychange', visibility);
    return () => { clear(); observer.disconnect(); document.removeEventListener('visibilitychange', visibility); };
  }, [placement.id]);
  return <div ref={ref} style={{ minWidth: 260 }}>
    {placement.product ? <ProductCardView product={placement.product} /> : <OrganizationCardView card={placement.business} />}
  </div>;
}

export function SponsoredPlacements({ lang }: { lang: string }) {
  const { t } = useTranslation();
  const signedIn = useAppSelector(s => s.session.status) === 'authenticated';
  const [placements, setPlacements] = useState<Placement[]>([]);
  useEffect(() => {
    setPlacements([]);
    if (!signedIn) return;
    const controller = new AbortController();
    void promotionApi.placements(lang, controller.signal).then(setPlacements).catch(() => {});
    return () => controller.abort();
  }, [signedIn, lang]);
  if (!signedIn || placements.length === 0) return null;
  return <section className="hub-section" aria-label={t('promotions.sponsored', 'Sponsored')}>
    <div className="hub-section-head"><h2>{t('promotions.sponsored', 'Sponsored')}</h2></div>
    <div className="hub-rail featured">{placements.map(p => <SponsoredCard key={p.id} placement={p} />)}</div>
  </section>;
}
